// High-level functions for manipulating and interpreting
// a compute graph.
//
// User code (UI) uses the functions in this file to manipulate and
// interact with the graph.

import * as _ from 'lodash';

import * as Graph from './graph';
import * as Types from './model/types';
import * as CGTypes from './types';
import * as StringUtils from './utils/string';
import * as TypeHelpers from './model/typeHelpers';
import {mapValues} from 'lodash';
import {Client} from './client';
import {taggableStrip} from './opTypes';

// Functions here assume that all ops have at least one input.
// Because functions must take at least one argument,
// otherwise, they're not a function, they're a constant. (Note:
// in normal programming languages, its perfectly acceptable to
// have zero-argument functions, but since we don't allow
// side-effects, they'd be useless here).

// For editing we use a graph definition that allows void nodes
// in op inputs

export function opInputsAreNonVoid(
  opInputs: CGTypes.EditingOpInputs
): opInputs is Types.OpInputs {
  return !Object.values(opInputs).some(n => n.nodeType === 'void');
}

export function nodeIsExecutable(
  node: CGTypes.EditingNode
): node is Types.NodeOrVoidNode {
  const voidCount = filterNodes(node, n => n.nodeType === 'void').length;
  return voidCount === 0 || (voidCount === 1 && node.nodeType === 'void');
}

export function isDotChainedOp(op: CGTypes.EditingOp) {
  const argValues = Object.values(op.inputs);
  const isChainType = Graph.getOpDef(op.name).renderInfo.type === 'chain';

  if (isChainType) {
    // these used to be the actual boolean test before `renderInfo` was introduced
    // they seem reasonable to keep around as sanity checks
    if (
      argValues.length === 0 ||
      !['var', 'output'].includes(argValues[0].nodeType)
    ) {
      throw new Error(
        `op ${op.name} is declared as a chain, but doesn't take appropriate inputs.`
      );
    }
  }

  return isChainType;
}

export function isBinaryOp(op: {name: string}) {
  return Graph.getOpDef(op.name).renderInfo.type === 'binary';
}

export function isFunctionOp(op: {name: string}) {
  return Graph.getOpDef(op.name).renderInfo.type === 'function';
}

export function isBracketsOp(op: {name: string}) {
  return Graph.getOpDef(op.name).renderInfo.type === 'brackets';
}

export function isChainedOp(op: CGTypes.EditingOp) {
  // of the form a.(x, y, z) or a.[x]
  return isDotChainedOp(op) || isBracketsOp(op);
}

export function isEditingNode(
  nodeOrOp: CGTypes.EditingNode | CGTypes.EditingOp
): nodeOrOp is CGTypes.EditingNode {
  return 'nodeType' in nodeOrOp;
}

export function isEditingOp(
  nodeOrOp: CGTypes.EditingNode | CGTypes.EditingOp
): nodeOrOp is CGTypes.EditingOp {
  return !isEditingNode(nodeOrOp);
}

export function binaryOpSymbol(op: {name: string}) {
  const opDef = Graph.getOpDef(op.name);

  if (opDef.renderInfo.type !== 'binary') {
    throw new Error(`op ${op.name} is not a binary op`);
  }

  return opDef.renderInfo.repr;
}

// low to high
const opPrecedences = [
  [
    'number-equal',
    'number-notEqual',
    'number-less',
    'number-greater',
    'number-lessEqual',
    'number-greaterEqual',
  ],
  ['number-add', 'number-sub'],
  ['number-mult', 'number-div'],
];
function getOpPrecedence(opName: string) {
  const index = opPrecedences.findIndex(opNames => opNames.includes(opName));

  // ops without a defined precedence are all assumed to have the highest
  // possible precedence
  return index === -1 ? opPrecedences.length : index;
}

/**
 * Return true if the given op needs parentheses to clarify the order of
 * operations given its position in graph.
 */
export function opNeedsParens(
  op: CGTypes.EditingOp,
  graph: CGTypes.EditingNode
) {
  if (!isBinaryOp(op)) {
    return false;
  }

  let outputOfOp: CGTypes.EditingOutputNode<Types.Type> | undefined;

  try {
    outputOfOp = findOutputOf(op, graph);
  } catch (e) {
    // findOutputOf will fail if the op is not currently in the graph
    // (which can happen when logging an op we're about to insert)
    //
    // this is actually fine -- it just means we don't need parens
  }

  if (!outputOfOp) {
    return false;
  }

  const {outputNode: consumingOutputNode, argIndex} =
    findConsumingOp(outputOfOp, graph) || {};

  if (!consumingOutputNode) {
    return false;
  }

  if (
    isDotChainedOp(consumingOutputNode.fromOp) ||
    isBracketsOp(consumingOutputNode.fromOp)
  ) {
    // these are special cases

    if (argIndex === 0) {
      // if we are the first argument, we should always be parenthesized
      // (given that we're a binary op, which we've already asserted above)

      // e.g. (x + y).avg() or
      //      (x + 1)[1]
      return true;
    }

    // if we're another argument, we should never be parenthesized

    // e.g. x.func(y + z, a + b) or
    //      x[y + z]
    // in both cases, the addition operators don't need parentheses
    return false;
  }

  const ourPrecedence = getOpPrecedence(op.name);

  // which arg could potentially be associated with the
  // consuming op, instead of us?
  let possiblyAmbiguousArg: CGTypes.EditingNode;
  let possibleClaimingArgNameInConsumer: string;

  if (argIndex === 0) {
    // we're the lhs, so the parent would "take" our rhs
    // for its lhs instead
    // e.g., we are:
    // 3 + 4
    // in
    // (3 + 4) / 5
    // and we need to distinguish ourselves from
    // 3 + 4 / 5
    possiblyAmbiguousArg = op.inputs.rhs;
    possibleClaimingArgNameInConsumer = 'lhs';
  } else {
    // we're the rhs, so the parent would "take" our lhs
    // for its rhs instead
    // e.g., we are:
    // 3 + 4
    // in
    //  5 / (3 + 4)
    // and we need to distinguish ourselves from
    // 5 / 4 + 4
    possiblyAmbiguousArg = op.inputs.lhs;
    possibleClaimingArgNameInConsumer = 'rhs';
  }

  // an alternate parenthesization would "move" one of our args to the
  // consuming expression -- here we check if that move would satisfy
  // the consumer's type constraints for inputs.

  // if not, there's no need to parenthesize: the types make the order of
  // operations unambiguous
  const consumingOpDef = Graph.getOpDef(consumingOutputNode.fromOp.name);
  const consumerCouldStealArg = nodeIsValidAsNamedArg(
    consumingOpDef,
    possibleClaimingArgNameInConsumer,
    possiblyAmbiguousArg
  );

  // apply parentheses if:
  // * the consuming operator has a higher level of precedence, AND
  // * the alternate parenthesization implied by the level of precedence would
  //   be valid
  return (
    getOpPrecedence(consumingOutputNode.fromOp.name) > ourPrecedence &&
    consumerCouldStealArg
  );
}

export function isFunctionType(
  maybeFunctionType: Types.Type
): maybeFunctionType is Types.FunctionType {
  return (
    typeof maybeFunctionType === 'object' &&
    maybeFunctionType.type === 'function'
  );
}

export function isFunctionLiteral(
  maybeFunction: CGTypes.EditingNode
): maybeFunction is Types.ConstNode<Types.FunctionType> {
  return (
    maybeFunction.nodeType === 'const' && isFunctionType(maybeFunction.type)
  );
}

// post-order traversal
export function mapNodes(
  node: CGTypes.EditingNode,
  mapFn: (inNode: CGTypes.EditingNode) => CGTypes.EditingNode,
  excludeFnBodies?: boolean
): CGTypes.EditingNode {
  if (node.nodeType === 'output') {
    // TODO: remove this jank
    if (node.fromOp.name === 'internal-lambdaClosureArgBridge') {
      return node;
    }
    const newInputs = _.mapValues(node.fromOp.inputs, inNode => {
      const mappedNode = mapNodes(inNode, mapFn, excludeFnBodies);
      // if (mappedNode.nodeType === 'void') {
      //   throw new Error('encountered void node while mapping');
      // }
      return mappedNode;
    });
    // Only replace node if an input has changed.
    // This way you can implement a findAndReplaceNode with mapNodes.
    let replace = false;
    for (const argName of Object.keys(node.fromOp.inputs)) {
      if (newInputs[argName] !== node.fromOp.inputs[argName]) {
        replace = true;
      }
    }
    if (!replace) {
      return mapFn(node);
    } else {
      return mapFn({
        ...node,
        fromOp: {
          ...node.fromOp,
          inputs: newInputs as any,
        },
      });
    }
  } else if (isFunctionLiteral(node) && !excludeFnBodies) {
    const newBody = mapNodes(node.val, mapFn, excludeFnBodies);

    if (!nodesEqual(node.val, newBody)) {
      return mapFn({
        ...node,
        val: newBody,
      });
    }

    return mapFn(node);
  } else {
    return mapFn(node);
  }
}

export async function mapNodesAsync(
  node: CGTypes.EditingNode,
  mapFn: (inNode: CGTypes.EditingNode) => Promise<CGTypes.EditingNode>,
  excludeFnBodies?: boolean
): Promise<CGTypes.EditingNode> {
  if (node.nodeType === 'output') {
    // TODO: remove this jank
    if (node.fromOp.name === 'internal-lambdaClosureArgBridge') {
      return Promise.resolve(node);
    }
    const newInputs = _.mapValues(node.fromOp.inputs, inNode => {
      const mappedNode = mapNodesAsync(inNode, mapFn, excludeFnBodies);
      // if (mappedNode.nodeType === 'void') {
      //   throw new Error('encountered void node while mapping');
      // }
      return mappedNode;
    });
    // Only replace node if an input has changed.
    // This way you can implement a findAndReplaceNode with mapNodesAsync.
    let replace = false;
    for (const argName of Object.keys(node.fromOp.inputs)) {
      if ((await newInputs[argName]) !== node.fromOp.inputs[argName]) {
        replace = true;
      }
    }
    if (!replace) {
      return mapFn(node);
    } else {
      const resolvedInputs = _.fromPairs(
        _.zip(
          Object.keys(newInputs),
          await Promise.all(Object.values(newInputs))
        )
      );
      return mapFn({
        ...node,
        fromOp: {
          ...node.fromOp,
          inputs: resolvedInputs as any,
        },
      });
    }
  } else if (isFunctionLiteral(node) && !excludeFnBodies) {
    const newBody = await mapNodesAsync(node.val, mapFn, excludeFnBodies);

    if (!nodesEqual(node.val, newBody)) {
      return mapFn({
        ...node,
        val: newBody,
      });
    }

    return mapFn(node);
  } else {
    return mapFn(node);
  }
}

export function expandAll(
  context: Client,
  node: CGTypes.EditingNode,
  frame: {[argName: string]: Types.Node}
): Promise<CGTypes.EditingNode> {
  return mapNodesAsync(node, async checkNode => {
    if (checkNode.nodeType === 'output') {
      const opDef = Graph.getOpDef(checkNode.fromOp.name);
      if (Graph.opDefIsGeneratedWeave(opDef)) {
        const expandedNode = await expandGeneratedWeaveOp(
          context,
          opDef,
          checkNode as any,
          frame
        );
        const result = await expandAll(context, expandedNode as any, frame);
        return result;
      } else if (Graph.opDefIsWeave(opDef)) {
        const expandedNode = expandWeaveOp(opDef, checkNode as any);
        const result = await expandAll(context, expandedNode as any, frame);
        return result;
      }
    }
    return Promise.resolve(checkNode);
  });
}

export function findChainedAncestor(
  node: Types.Node,
  findFn: (inNode: Types.Node) => boolean,
  whileFn: (inNode: Types.Node) => boolean
): Types.Node | undefined {
  if (findFn(node)) {
    return node;
  }
  if (!whileFn(node)) {
    return undefined;
  }
  if (node.nodeType !== 'output') {
    return undefined;
  }
  return findChainedAncestor(
    Object.values(node.fromOp.inputs)[0],
    findFn,
    whileFn
  );
}

export function findChainedAncestors(
  node: Types.Node,
  findFn: (inNode: Types.Node) => boolean
): Types.Node[] {
  const results: Types.Node[] = [];
  if (findFn(node)) {
    results.push(node);
  }
  if (node.nodeType !== 'output') {
    return results;
  }
  return results.concat(
    findChainedAncestors(Object.values(node.fromOp.inputs)[0], findFn)
  );
}

export function findContainingBracketNode(
  currentNodeOrOp: CGTypes.EditingNode | CGTypes.EditingOp | undefined,
  graph: CGTypes.EditingNode
): CGTypes.EditingNode | null {
  if (!currentNodeOrOp) {
    // we've hit the root -- there's no containing bracket
    return null;
  }

  // we passed in an op -- move to its corresponding node to continue
  // searching
  if (isEditingOp(currentNodeOrOp)) {
    return findContainingBracketNode(
      findOutputOf(currentNodeOrOp, graph),
      graph
    );
  }

  if (
    currentNodeOrOp.nodeType === 'output' &&
    isBracketsOp(currentNodeOrOp.fromOp)
  ) {
    // we've found a containing bracket
    return currentNodeOrOp;
  }

  const ancestor = findConsumingOp(currentNodeOrOp, graph)?.outputNode;

  return findContainingBracketNode(ancestor, graph);
}

// Throws an error if not found
export function replaceNode(
  node: CGTypes.EditingNode,
  toReplace: CGTypes.EditingNode,
  replaceWith: CGTypes.EditingNode
): CGTypes.EditingNode {
  const result = mapNodes(node, checkNode =>
    checkNode === toReplace ? replaceWith : checkNode
  );
  if (result === node) {
    // nothing changed!
    throw new Error('didnt find node to replace');
  }
  return result;
}

export function maybeReplaceNode(
  node: CGTypes.EditingNode,
  toReplace: CGTypes.EditingNode,
  replaceWith: CGTypes.EditingNode
): CGTypes.EditingNode {
  return mapNodes(node, checkNode =>
    checkNode === toReplace ? replaceWith : checkNode
  );
}

export function replaceOp(
  node: CGTypes.EditingNode,
  toReplace: CGTypes.EditingOp,
  replaceWith: CGTypes.EditingOp
): CGTypes.EditingNode {
  const outputNodes = filterNodes(
    node,
    n => n.nodeType === 'output' && n.fromOp === toReplace
  );
  if (outputNodes.length !== 1) {
    throw new Error(
      `Expected exactly 1 output for op ${opToString(
        toReplace,
        node
      )} in ${toString(node)}`
    );
  }

  const nodeToReplace = outputNodes[0] as CGTypes.EditingOutputNode;
  const nodeReplaceWith: CGTypes.EditingOutputNode = {
    ...nodeToReplace,
    fromOp: replaceWith,
  };

  return replaceNode(node, nodeToReplace, nodeReplaceWith);
}

export function expandWeaveOp(
  opDef: CGTypes.OpDefWeave,
  node: Types.OutputNode
) {
  return callFunction(opDef.body, node.fromOp.inputs as any);
}

export function expandGeneratedWeaveOp(
  context: Client,
  opDef: CGTypes.OpDefGeneratedWeave,
  node: Types.OutputNode,
  frame: {[argName: string]: Types.Node}
) {
  const macroFrame = {
    ...frame,
    ...node.fromOp.inputs,
  };
  return opDef.expansion(
    node.fromOp.inputs,
    (innerNode: Types.Node) =>
      refineEditingNode(context, innerNode, macroFrame) as any
  );
}

function nodesEqual(node1: CGTypes.EditingNode, node2: CGTypes.EditingNode) {
  if (node1.nodeType === 'void' && node2.nodeType === 'void') {
    return true;
  }
  if (!_.isEqual(node1.type, node2.type)) {
    return false;
  }
  if (node1.nodeType === 'const' && node2.nodeType === 'const') {
    // Equal if shallow equal for now
    return node1.val === node2.val;
  } else if (node1.nodeType === 'var' && node2.nodeType === 'var') {
    return node1.varName === node2.varName;
  } else if (node1.nodeType === 'output' && node2.nodeType === 'output') {
    const op1 = node1.fromOp;
    const op2 = node2.fromOp;
    if (op1.name !== op2.name) {
      return false;
    }
    for (const key of Array.from(
      new Set(Object.keys(op1.inputs).concat(Object.keys(op2.inputs)))
    )) {
      if (op1.inputs[key] !== op2.inputs[key]) {
        return false;
      }
    }
    return true;
  }
  return false;
}

// Preserves shallow-equality when it can, based on nodesEqual above.
// nodesEqual uses shallow equality to compare op inputs.
export async function refineEditingNode(
  client: Client,
  node: CGTypes.EditingNode,
  frame: {[argName: string]: Types.Node}
): Promise<CGTypes.EditingNode> {
  if (node.nodeType === 'void') {
    return node;
  } else if (node.nodeType === 'var') {
    const dereffed = frame?.[node.varName];
    if (dereffed != null) {
      const newNode = {...node, type: dereffed.type};

      if (nodesEqual(node, newNode)) {
        return node;
      }

      return newNode;
    }
    return node;
  } else if (node.nodeType === 'const') {
    return node;
  } else if (node.nodeType === 'output') {
    const op = node.fromOp;
    const opDef = Graph.getOpDef(op.name);

    // Otherwise improve all of this ops inputs first.
    const argNames = Object.keys(op.inputs);
    const argValues = Object.values(op.inputs);
    const argValuesRefined = await Promise.all(
      argValues.map(async (n, i) => {
        if (isFunctionLiteral(n)) {
          const {getFunctionFrame} = await import('./refineHelpers');

          const functionBodyFrame = getFunctionFrame(
            op.name,
            argValues[0],
            frame
          );

          const refinedFunctionBody = await refineEditingNode(
            client,
            n.val,
            functionBodyFrame
          );

          const expectedFunctionType = Object.values(opDef.inputTypes)[i];

          let inputTypes = n.type.inputTypes;
          if (
            isFunctionType(expectedFunctionType) &&
            Object.values(n.type.inputTypes).length === 0
          ) {
            // older versions of CG allowed functions with no arguments and would
            // infer the expected arguments from the opDef -- if we encounter such
            // a function, let's convert it to the new style:
            inputTypes = expectedFunctionType.inputTypes;

            // the function frame has even more specific type information for these
            // arguments:
            for (const key of Object.keys(inputTypes)) {
              if (functionBodyFrame[key]) {
                inputTypes[key] = functionBodyFrame[key].type;
              }
            }
          }

          const refinedFunctionLiteral = {
            ...n,
            type: {
              ...n.type,
              inputTypes,
              outputType: refinedFunctionBody.type,
            },
            val: refinedFunctionBody,
          };

          if (nodesEqual(n, refinedFunctionLiteral)) {
            return n;
          }

          return refinedFunctionLiteral;
        }

        return refineEditingNode(client, n, frame);
      })
    );
    const inputsRefined: CGTypes.EditingOpInputs = {};
    argNames.forEach((name, i) => (inputsRefined[name] = argValuesRefined[i]));

    const hasValidInput = opInputsAreValid(inputsRefined, opDef);

    // If any input is void, the type of this node is void, and it can't be
    // refined. Just return it.
    if (!hasValidInput || !opInputsAreNonVoid(inputsRefined)) {
      const newNode = {
        nodeType: 'output' as const,
        fromOp: {
          ...op,
          inputs: inputsRefined,
        },
        type: 'invalid' as const,
      };

      if (nodesEqual(node, newNode)) {
        return node;
      }

      return newNode;
    }

    const nodeWithRefinedInputs = {
      nodeType: 'output' as const,
      type: node.type,
      fromOp: {
        ...op,
        inputs: inputsRefined,
      },
    };
    if (Graph.opDefIsWeave(opDef)) {
      const expandedOutput = await refineEditingNode(
        client,
        expandWeaveOp(opDef, nodeWithRefinedInputs),
        frame
      );
      return {...nodeWithRefinedInputs, type: expandedOutput.type};
    } else if (Graph.opDefIsGeneratedWeave(opDef)) {
      const expandedOutput = await refineEditingNode(
        client,
        // For now, this is not too bad, but if we have a lot of these in an
        // expression, it could get non-performant as it has to expand the tree
        // each time. Leaving the cheaper one below for now.
        await expandAll(client, nodeWithRefinedInputs, frame),
        // (await expandGeneratedWeaveOp(
        //   client,
        //   opDef,
        //   nodeWithRefinedInputs,
        //   frame
        // )) as any,
        frame
      );
      return {...nodeWithRefinedInputs, type: expandedOutput.type};
    } else if (opDef.refineNode != null) {
      // Make an executable version of nodeWithRefinedInputs by dereffing any
      // variables.
      const dereffed = dereferenceVariables(nodeWithRefinedInputs, frame ?? {});
      const dereffedNode = dereffed.node;
      if (dereffedNode.nodeType !== 'output') {
        throw new Error('invalid');
      }
      const dereffedOp = dereffedNode.fromOp;
      const dereffedOpInputs = dereffedOp.inputs;
      if (!opInputsAreNonVoid(dereffedOpInputs)) {
        // We already dealt with this above
        throw new Error('invalid');
      }
      const executableOp = {
        ...dereffedOp,
        inputs: dereffedOpInputs,
      };
      const executableNode = {
        nodeType: 'output' as const,
        fromOp: executableOp,
        type: 'none' as const,
      };

      const resolvedNode = await opDef.refineNode(
        nodeWithRefinedInputs,
        executableNode,
        client
      );

      if (nodesEqual(node, resolvedNode)) {
        return node;
      } else {
        return resolvedNode;
      }
    } else {
      const newNode2 = {
        ...nodeWithRefinedInputs,
        type:
          typeof opDef.outputType === 'function'
            ? opDef.outputType(inputsRefined)
            : opDef.outputType,
      };

      if (nodesEqual(node, newNode2)) {
        return node;
      }

      return newNode2;
    }
  } else {
    throw new Error('invalid');
  }
}

function nodeIsValidAsNamedArg(
  opDef: CGTypes.OpDef,
  argName: string,
  possibleArgValue: CGTypes.EditingNode
) {
  const inputType = opDef.inputTypes[argName];

  // if inputType is null, allow all types! This is to handle the current
  // manyX ugliness in some ops (ops that take many args). But other bugs
  // may result :(
  return (
    inputType == null || Types.isAssignableTo(possibleArgValue.type, inputType)
  );
}

export function opInputsAreValid(
  inputs: CGTypes.EditingOpInputs,
  opDef: CGTypes.OpDef
) {
  let hasValidInput = true;
  for (const [argName, argValue] of Object.entries(inputs)) {
    if (!nodeIsValidAsNamedArg(opDef, argName, argValue)) {
      hasValidInput = false;
    }
  }
  return hasValidInput;
}

/** You must catch errors from function, and rethrow them in the render
 * thread.
 */
export async function refineNode(
  client: Client,
  node: Types.Node,
  frame: {[argName: string]: Types.Node}
): Promise<Types.Node> {
  if (node.nodeType !== 'output') {
    return node;
  }
  return (await refineEditingNode(client, node, frame)) as Types.Node;
}

export async function replaceNodeAndUpdateDownstreamTypes(
  context: Client,
  graph: CGTypes.EditingNode,
  toReplace: CGTypes.EditingNode,
  replaceWith: CGTypes.EditingNode,
  frame: {[argName: string]: Types.Node}
) {
  // This function is tricky, we want to ensure that replaceWith
  // is actually in the graph (by reference) at the end of this call
  // but refineEditingNodeType can replace all output nodes. So if
  // we have an output node, do something special, swap it with a
  // variable, and then swap at back after refineEditingNodeType.

  graph = replaceNode(
    graph,
    toReplace,
    replaceWith.nodeType !== 'output'
      ? replaceWith
      : Graph.varNode(replaceWith.type, '_swapVar')
  );

  graph = await refineEditingNode(context, graph, frame);

  if (replaceWith.nodeType === 'output') {
    graph = dereferenceVariables(graph, {
      _swapVar: replaceWith,
    }).node;
  }
  return graph;
}

/**
 * Return editable expression nodes and ops in the order they're displayed.
 *
 * Note that the tail node does NOT appear at the end, since this doesn't
 * make sense in a recursive context. That is, given:
 *
 * 3 + x + 4
 *
 * the text ordered nodes are:
 *
 * 3, +, x, +, 4
 */
export function textOrderedNodesAndOps(
  node: CGTypes.EditingNode
): Array<CGTypes.EditingNode | CGTypes.EditingOp> {
  if (isFunctionLiteral(node)) {
    return textOrderedNodesAndOps(node.val);
  }

  if (node.nodeType !== 'output') {
    return [node];
  }
  const orderedInputs = Object.values(node.fromOp.inputs);

  if (isBinaryOp(node.fromOp) || isDotChainedOp(node.fromOp)) {
    // a + b
    // a.foo(b)

    // in both cases, the editing order is: first argument, operator, rest of arguments
    return [
      ...textOrderedNodesAndOps(orderedInputs[0]),
      node.fromOp,
      ...orderedInputs.slice(1).flatMap(textOrderedNodesAndOps),
    ];
  }

  if (isBracketsOp(node.fromOp)) {
    // a[b]
    // the TRUE editing order would be something like:
    // first argument, operator, second argument, operator again (but with with the cursor after the argument,
    // which we have no way to express at present)
    //
    // in practice, focusing brackets ops isn't a very good experience right now, so we'll just skip them
    // and pretend the editing order is: first argument, second argument

    return orderedInputs.flatMap(textOrderedNodesAndOps);
  }

  if (isFunctionOp(node.fromOp)) {
    // someFunc(a, b)

    // the editing order is: operator, rest of arguments
    return [node.fromOp, ...orderedInputs.flatMap(textOrderedNodesAndOps)];
  }

  console.warn(
    `ordering not implemented for op: ${node.fromOp.name} with render type: ${
      Graph.getOpDef(node.fromOp.name).renderInfo.type
    }`
  );

  // fall back on binary/chain-like ordering if none is specified
  return [
    ...textOrderedNodesAndOps(orderedInputs[0]),
    node.fromOp,
    ...orderedInputs.slice(1).flatMap(textOrderedNodesAndOps),
  ];
}

export function getNextNodeOrOpInTextOrder(
  focusedNodeOrOp: CGTypes.EditingNode | CGTypes.EditingOp,
  graph: CGTypes.EditingNode
): CGTypes.EditingNode | CGTypes.EditingOp | null {
  if (focusedNodeOrOp === graph) {
    // we're focusing the tail of the graph -- we can't go any further
    return null;
  }

  const textOrdered = textOrderedNodesAndOps(graph);

  const focusIndex = textOrdered.findIndex(
    n =>
      n === focusedNodeOrOp ||
      // we're not including output nodes in the text order right now,
      // so if an output node is focused we should act as if its
      // op is focused instead
      n === (focusedNodeOrOp as CGTypes.EditingOutputNode)?.fromOp
  );

  if (focusIndex === -1) {
    console.warn(
      `Getting next node or op: Can't find focused node or op ${JSON.stringify(
        focusedNodeOrOp,
        undefined,
        2
      )} in sequence ${JSON.stringify(
        textOrdered,
        undefined,
        2
      )}. Selecting the first node.`
    );
    return textOrdered[0];
  }

  if (focusIndex === textOrdered.length - 1) {
    // we're at the last ordered node, so the user presumably wants to append
    // new nodes to the graph -- we should go to the tail
    return graph;
  }

  return textOrdered[focusIndex + 1];
}

export function getPrevNodeOrOpInTextOrder(
  focusedNodeOrOp: CGTypes.EditingNode | CGTypes.EditingOp,
  graph: CGTypes.EditingNode
): CGTypes.EditingNode | CGTypes.EditingOp | null {
  const textOrdered = textOrderedNodesAndOps(graph);

  if (focusedNodeOrOp === graph) {
    // we're focusing the tail of the graph, which won't appear in the text
    // order -- we should go to the last node in text order (if there are any)
    if (textOrdered.length > 0) {
      return textOrdered[textOrdered.length - 1];
    }

    return null;
  }

  const focusIndex = textOrdered.findIndex(
    n =>
      n === focusedNodeOrOp ||
      // we're not including output nodes in the text order right now,
      // so if an output node is focused we should act as if its
      // op is focused instead
      n === (focusedNodeOrOp as CGTypes.EditingOutputNode)?.fromOp
  );

  if (focusIndex === -1) {
    console.warn(
      `Getting prev node or op: Can't find focused node or op ${JSON.stringify(
        focusedNodeOrOp,
        undefined,
        2
      )} in sequence ${JSON.stringify(
        textOrdered,
        undefined,
        2
      )}. Selecting the first node.`
    );
    return textOrdered[0];
  }

  return textOrdered[focusIndex - 1] || null;
}

// Dereference variables including walking const functions.
export function dereferenceVariables(
  functionNode: CGTypes.EditingNode,
  variables: {[argName: string]: CGTypes.EditingNode}
): {node: Types.Node; usedVarNames: string[]} {
  const usedVarNames = new Set<string>();
  // walk function node, finding ops that have var inputs
  const node = mapNodes(
    functionNode,
    n => {
      if (n.nodeType === 'var') {
        const swapNode = variables[n.varName];
        if (swapNode == null) {
          return n;
        }
        usedVarNames.add(n.varName);
        return swapNode;
      } else if (n.nodeType === 'const' && Types.isFunction(n.type)) {
        const fnInputTypes = n.type.inputTypes;
        const innerVariables = {...variables};
        if (fnInputTypes != null) {
          // If the function has declared input types, don't dereference those
          // variables, they will be provided at the time the function is called!
          for (const paramName of Object.keys(fnInputTypes)) {
            delete innerVariables[paramName];
          }
        }
        const {node: dereffedFn, usedVarNames: fnUsedVars} =
          dereferenceVariables(n.val, innerVariables);
        for (const usedVar of fnUsedVars) {
          usedVarNames.add(usedVar);
        }
        // If they're the same, return the same exact object! We
        // rely on memory equality in lots of places to make things
        // fast
        if (n.val === dereffedFn) {
          return n;
        }
        return {
          ...n,
          val: dereffedFn,
        };
      }
      return n;
    },
    true
  ) as Types.Node; // can cast since we're feeding Nodes in

  // If we used a variable, do another pass. The new expression may
  // refer to other variables.
  //
  // it's possible that variables reference *themselves*, which
  // would cause infinite recursion -- we use !nodesEqual() to guard
  // against that
  if (usedVarNames.size !== 0 && !nodesEqual(functionNode, node)) {
    const nextResult = dereferenceVariables(node, variables);
    return {
      node: nextResult.node,
      usedVarNames: Array.from(usedVarNames).concat(nextResult.usedVarNames),
    };
  }
  return {node, usedVarNames: Array.from(usedVarNames)};
}

// Given a set of arguments, which are represented as nodes,
// and a function, which is a node that terminates at variables or
// constants, construct a new node that represents the function call

// TODO: callFunction should be updated to take a defineFunction result
// as its argument, and do call by position instead of argument name.
// This will allow users to name their function variables whatever they
// want, instead of what the caller expects.
// At the same time we should fix up
export function callFunction(
  functionNode: Types.NodeOrVoidNode,
  inputs: {[argName: string]: Types.Node}
): Types.Node {
  const {node} = dereferenceVariables(functionNode, inputs);
  // Don't do this check, we sometimes callFunction with a root function and
  // nothing in the frame

  // if (usedVarNames.length === 0) {
  //   throw new Error(
  //     'No function inputs used in call, this is probably a programming error'
  //   );
  // }
  return node as Types.Node;
}

// Given a node that represents a function (a node with at least
// one variable ancestor), update the types of the variables, given
// inputs that have the desired types.
export function updateFunctionInputTypes(
  functionNode: Types.Node,
  inputs: {[argName: string]: Types.Node}
) {
  const usedVarNames = new Set<string>();
  // walk function node, finding ops that have var inputs
  const node = mapNodes(functionNode, n => {
    if (n.nodeType === 'var') {
      const inputForVar = inputs[n.varName];
      if (inputForVar == null) {
        throw new Error(
          'variable not in frame: ' +
            n.varName +
            ', available: ' +
            Object.keys(inputs)
        );
      }
      usedVarNames.add(n.varName);
      return {
        ...n,
        type: inputs[n.varName].type,
      };
    }
    return n;
  }) as Types.Node;
  return {node, usedVarNames: Array.from(usedVarNames)};
}

export function opDisplayName(op: {name: string}): string {
  if (isBinaryOp(op)) {
    return binaryOpSymbol(op);
  }
  if (op.name.indexOf('-') !== -1) {
    return StringUtils.splitOnce(op.name, '-')[1] as string;
  }
  return op.name;
}

const PRODUCIBLE_TYPES: Types.Type[] = [
  Types.functionType({}, 'any'),
  'string',
  'number',
  'boolean',
  'none',
  Types.maybe('string'),
  Types.maybe('number'),
  Types.maybe('boolean'),
];
function isProducibleType(type: Types.Type) {
  return PRODUCIBLE_TYPES.find(t => Types.isAssignableTo2(type, t)) != null;
}

/**
 * Returns the list of op definitions that could replace op toReplace.
 *
 * Matches based on the input and return types of the ops.
 */
export function validReplacementOps(toReplace: CGTypes.EditingOp) {
  const getOpDefOutputType = (
    opDef: CGTypes.OpDef,
    inputs?: CGTypes.EditingOpInputs
  ): Types.Type => {
    if (typeof opDef.outputType !== 'function') {
      return opDef.outputType;
    }

    if (inputs && opInputsAreNonVoid(inputs)) {
      return opDef.outputType(inputs);
    }

    // we have no inputs or incomplete inputs, so we can't compute
    // the concrete type and we'll have to use 'any' instead.
    return 'any';
  };

  const orderedInputs = Object.values(toReplace.inputs);
  const orderedInputTypes = orderedInputs.map(input => input.type);
  const outputType = getOpDefOutputType(
    Graph.getOpDef(toReplace.name),
    toReplace.inputs
  );

  return Object.values(Graph.allGlobalOps()).filter(opDef => {
    if (opDef.hidden) {
      return false;
    }

    // validate that the inputs match
    const opDefOrderedInputTypes = Object.values(opDef.inputTypes);

    if (opDefOrderedInputTypes.length !== orderedInputTypes.length) {
      return false;
    }

    for (const [index, inputType] of orderedInputTypes.entries()) {
      const opDefInputType = opDefOrderedInputTypes[index];

      if (!Types.isAssignableTo(inputType, opDefInputType)) {
        return false;
      }
    }

    // try to validate that the return types match:
    const opDefArgNames = Object.keys(opDef.inputTypes);
    const newTypedInputs: CGTypes.EditingOpInputs = {};
    opDefArgNames.forEach(
      (name, i) => (newTypedInputs[name] = orderedInputs[i])
    );

    const opDefOutputType = getOpDefOutputType(opDef, newTypedInputs);

    // TODO: take node context into account here -- if the op is being consumed
    // by a node that can take multiple types, this line will incorrectly limit
    // us to only types that exactly match the old op's type
    return Types.isAssignableTo(outputType, opDefOutputType);
  });
}

export function isValidRootOp(opDef: CGTypes.OpDef) {
  return opDef.name.startsWith('root');
}

function availableOps() {
  return Object.values(Graph.allGlobalOps()).filter(opDef => !opDef.hidden);
}

export function rootOps() {
  return Object.values(availableOps()).filter(isValidRootOp);
}

export function availableOpsForChain(varNodeType: Types.Type): CGTypes.OpDef[] {
  return Object.values(availableOps()).filter(opDef => {
    if (isValidRootOp(opDef)) {
      return false;
    }
    const inputTypes = Object.values(opDef.inputTypes);
    const inputType0 = inputTypes[0];
    const remainingTypes = inputTypes.slice(1);
    return (
      Types.isAssignableTo2(varNodeType, inputType0) &&
      remainingTypes.every(isProducibleType)
    );
  });
}

export function pickSuggestions(objType: Types.Type): string[] {
  // Currently this returns all paths to leave, but its useful to
  // be able to fetch intermediate nodes as weTypes. TODO: fix
  objType = taggableStrip(objType);

  if (Types.isNullable(objType)) {
    objType = Types.nonNullable(objType);
  }
  if (Types.isTaggedValue(objType)) {
    objType = objType.value;
  }
  if (Types.isListLike(objType)) {
    objType = Types.listObjectType(objType);
  }
  if (Types.isTaggedValue(objType)) {
    objType = objType.value;
  }
  let keys: string[] = [];
  if (Types.isTypedDictLike(objType)) {
    const allPaths = TypeHelpers.allObjPaths(objType).map(pt =>
      pt.path.map(s => s.replace(new RegExp('\\.', 'g'), '\\.'))
    );
    // If we have nested paths that share the same tail key, included *. paths
    // in suggestions
    const sameLengthAndTailGroups = _.groupBy(
      allPaths,
      path => `${path.length}-${path[path.length - 1]}`
    );
    keys = Object.values(sameLengthAndTailGroups)
      .flatMap(paths => {
        if (paths.length === 1) {
          return paths;
        }
        const path0 = paths[0];
        const starredPath = _.range(path0.length - 1)
          .map(i => '*')
          .concat([path0[path0.length - 1]]);
        return [starredPath].concat(paths);
      })
      .map(path => path.join('.'));
  }
  return keys;
}

export function callOp(
  opName: string,
  inputs: CGTypes.EditingOpInputs
): CGTypes.EditingOutputNode {
  return {
    nodeType: 'output',
    type: 'any',
    fromOp: {
      name: opName,
      inputs,
    },
  };
}

export function callOpValid(
  opName: string,
  inputs: Types.OpInputs
): Types.Node {
  const opDef = Graph.getOpDef(opName);
  return {
    nodeType: 'output',
    type:
      typeof opDef.outputType === 'function'
        ? opDef.outputType(inputs)
        : opDef.outputType,
    fromOp: {
      name: opName,
      inputs,
    },
  };
}

export function findConsumingOp(
  node: CGTypes.EditingNode,
  graph: CGTypes.EditingNode
):
  | {argIndex: number; argName: string; outputNode: CGTypes.EditingOutputNode}
  | undefined {
  const consumingOps = filterNodes(graph as any, checkNode => {
    if (checkNode.nodeType === 'output') {
      const innerArgNodes = Object.values(checkNode.fromOp.inputs);
      return innerArgNodes.find(n => n === node) != null;
    }
    return false;
  });
  if (consumingOps.length === 0) {
    return undefined;
  }
  if (consumingOps.length > 1) {
    throw new Error('too many consuming ops, this is not an expression');
  }
  const consumingOpNode = consumingOps[0];
  if (consumingOpNode.nodeType !== 'output') {
    // impossible, we just checked above.
    throw new Error('invalid');
  }
  const argNames = Object.keys(consumingOpNode.fromOp.inputs);
  const argNodes = Object.values(consumingOpNode.fromOp.inputs);
  const argIndex = argNodes.findIndex(n => n === node);
  return {argIndex, argName: argNames[argIndex], outputNode: consumingOpNode};
}

export function findOutputOf(
  op: CGTypes.EditingOp,
  graph: CGTypes.EditingNode
): CGTypes.EditingOutputNode {
  const outputNodes = filterNodes(
    graph,
    node => node.nodeType === 'output' && node.fromOp === op
  );

  if (outputNodes.length !== 1) {
    // not using opToString here because it would recursively call findOutputOf
    throw new Error(
      `Expected exactly one output node for op ${op.name} in ${JSON.stringify(
        graph
      )}, but found ${outputNodes.length}`
    );
  }

  return outputNodes[0] as CGTypes.EditingOutputNode;
}

/**
 * Return the node representing the rightmost non-void element in the rendered
 * expression represented by node. Easier to understand by example -- see hl.test.ts.
 *
 * The intention is to return the thing that should "disappear" (from the user's
 * perspective), when they press backspace. Note that removing this element is
 * often more complicated than just voiding out its node -- see
 * expEditorState.tsx:deletePrev for details.
 */
export function rightMostToDelete(
  node: CGTypes.EditingNode
): CGTypes.EditingNode {
  if (isFunctionLiteral(node)) {
    if (Graph.isVoidNode(node.val)) {
      // the function body is empty: we should delete the entire
      // function literal:
      return node;
    }

    // the function body isn't empty -- we should delete its rightmost element
    return rightMostToDelete(node.val);
  }

  if (node.nodeType !== 'output') {
    // only output nodes and function literals have ancestors, so this is the
    // base case
    return node;
  }

  const op = node.fromOp;
  const argNodes = Object.values(op.inputs);

  if (isChainedOp(op)) {
    // either this unary op takes no arguments besides the previous chain,
    // or all arguments have already been voided out
    const argNodesAfterDotAreVoid =
      argNodes.slice(1).filter(argNode => argNode.nodeType !== 'void')
        .length === 0;

    if (argNodesAfterDotAreVoid) {
      return node;
    }
  } else if (isFunctionOp(op)) {
    // either this function takes no arguments,
    // or all arguments have already been voided out
    const argNodesAreVoid =
      argNodes.filter(argNode => argNode.nodeType !== 'void').length === 0;

    if (argNodesAreVoid) {
      return node;
    }
  }

  const rightMostArgNode = argNodes[argNodes.length - 1];
  return rightMostToDelete(rightMostArgNode);
}

// Given a function (a node which has at least one variable ancestor)
// update every node in the function graph with new types given
// inputs for the function.
export async function updateFunctionForInputs(
  client: Client,
  fnNode: Types.Node,
  inputs: {[argName: string]: Types.Node}
) {
  // First, just update the function's variable nodes with the new types.
  const {node: withUpdatedInputTypes, usedVarNames} = updateFunctionInputTypes(
    fnNode,
    inputs
  );
  if (usedVarNames.length === 0) {
    console.log('Update function for inputs error', toString(fnNode), inputs);
    throw new Error(
      'No function inputs used in call, this is probably a programming error'
    );
  }

  // Second, use improveOutputNode on the function's output to recursively
  // update all types within the function (including the final output node).
  if (withUpdatedInputTypes.nodeType !== 'output') {
    return Promise.resolve(withUpdatedInputTypes);
  }
  const improvedNode = await refineNode(client, withUpdatedInputTypes, inputs);
  return Promise.resolve(improvedNode);
}

export function toString(
  node: CGTypes.EditingNode,
  indent: number | null = 0,
  root?: CGTypes.EditingNode
): string {
  if (node.nodeType === 'const') {
    if (Types.isFunction(node.type)) {
      if (indent != null) {
        indent += 2;
      }
      const inputVars = Object.keys(node.type.inputTypes);
      // This is a function argument

      const fnString =
        '(' +
        _.uniq(inputVars).join(', ') +
        ') => ' +
        toString(node.val, 0, root);
      const lines =
        indent != null
          ? fnString
              .split('\n')
              .map((l, i) =>
                i === 0 ? l : StringUtils.indent(l, indent as number)
              )
              .join('\n')
          : fnString;
      return lines;
    } else if (node.type === 'none') {
      return 'None';
    }
    // NOTICE: JSON.stringify will transform single backslashes into double backslashes.
    // for example:
    // s = 'a\\b'
    // s.length // => 3 (the double backslash is needed to enter the raw value of 'a\b')
    // JSON.stringify(s) // => "a\\b" (see how it adds a slash)
    //
    // the following adjustment replaces all instances of `\\` with `\`.
    return StringUtils.indent(
      JSON.stringify(node.val).replace(/\\\\/g, '\\'),
      indent as number
    );
  } else if (node.nodeType === 'void') {
    return '';
  } else if (node.nodeType === 'var') {
    // TODO: Big hack, if we encounter a var, make it look like a function
    // call. This won't work at all in scenarios where we encounter more
    // than one var!
    // TODO: Broke this hack in this commit
    return `${node.varName}`;
  } else if (node.nodeType == null) {
    // TODO: in the join op `ops.ts#opJoin`, the return type has 6 keys,
    // 2 of which (arr1 and arr2) end up being objectTypes, but lack the
    // fromOp and nodeType keys. We should resolve that which would remove this
    // branch of the conditional.
    return '';
  } else {
    return opToString(node.fromOp, root || node, indent);
  }
}

export function filterNodes(
  node: CGTypes.EditingNode,
  filterFn: (inNode: CGTypes.EditingNode) => boolean,
  excludeFnBodies?: boolean
): CGTypes.EditingNode[] {
  let result: CGTypes.EditingNode[] = filterFn(node) ? [node] : [];
  if (
    node.nodeType === 'const' &&
    typeof node.type === 'object' &&
    node.type?.type === 'function' &&
    !excludeFnBodies
  ) {
    result = [...result, ...filterNodes(node.val, filterFn)];
  } else if (node.nodeType === 'output') {
    const childNodes = Object.values(node.fromOp.inputs).flatMap(inNode =>
      filterNodes(inNode, filterFn)
    );
    result = result.concat(childNodes);
  }
  return result;
}

export function opToString(
  op: CGTypes.EditingOp,
  graph: CGTypes.EditingNode,
  indent: number | null = 0
): string {
  const argValues = Object.values(op.inputs);
  const nodeToString = (node: CGTypes.EditingNode) => {
    return toString(node, indent, graph);
  };

  const opDef = Graph.getOpDef(op.name);

  switch (opDef.renderInfo.type) {
    case 'dictionaryLiteral':
      let res = '({\n';
      for (const argName of Object.keys(op.inputs)) {
        res += `  ${argName}: ${nodeToString(op.inputs[argName])},\n`;
      }
      res += '})';
      return res;
    case 'binary':
      const needsParens = opNeedsParens(op, graph);

      return `${needsParens ? '(' : ''}${nodeToString(argValues[0])} ${
        opDef.renderInfo.repr
      } ${nodeToString(argValues[1])}${needsParens ? ')' : ''}`;
    case 'brackets':
      return `${nodeToString(argValues[0])}[${nodeToString(argValues[1])}]`;
    case 'chain':
      const restOfArgs = argValues.slice(1);
      const unaryOpArgs =
        restOfArgs.length > 0
          ? `(${restOfArgs.map(nodeToString).join(', ')})`
          : '';
      return `${nodeToString(argValues[0])}${
        indent != null ? '\n  ' : ''
      }.${opDisplayName(op)}${unaryOpArgs}`;
    case 'function':
      const opArgs = argValues
        .map(nodeToString)
        .map(argLinesString =>
          indent != null
            ? argLinesString
                .split('\n')
                .map(v => StringUtils.indent(v, 2))
                .join('\n')
            : argLinesString
        )
        .join(`,${indent != null ? '\n' : ' '}`);
      return `${opDisplayName(op)}(${indent != null ? '\n' : ''}${opArgs})`;
    default:
      throw new Error(
        `op ${op.name} has invalid renderInfo.type ${
          (opDef.renderInfo as any).type
        }`
      );
  }
}

export function allVarsWillResolve(
  node: CGTypes.EditingNode,
  frame: {[argName: string]: Types.Node | true}
): boolean {
  switch (node.nodeType) {
    case 'var':
      return !!frame[node.varName];
    case 'output':
      return Object.values(node.fromOp.inputs).every(input =>
        allVarsWillResolve(input, frame)
      );
    case 'const':
      if (Types.isFunction(node.type)) {
        return allVarsWillResolve(node.val, {
          ...frame,

          // true indicates variable is a function argument, and will be
          // populated at call-time
          ...mapValues(node.type.inputTypes, () => true),
        });
      }
      return true;
    default:
      return true;
  }
}

export function couldBeReplacedByType(
  node: CGTypes.EditingNode,
  graph: CGTypes.EditingNode,
  replacementType: Types.Type
) {
  const consumer = findConsumingOp(node, graph);

  return (
    consumer != null &&
    Types.isAssignableTo2(
      replacementType,
      Graph.getOpDef(consumer.outputNode.fromOp.name).inputTypes[
        consumer.argName
      ]
    )
  );
}

// These functions should produce the same result as ExpressionView

const simpleArgsString = (args: CGTypes.EditingOpInputs): string =>
  '(' +
  Object.values(args)
    .map(v => simpleNodeString(v))
    .join(', ') +
  ')';

const simpleOpString = (op: CGTypes.EditingOp): string => {
  const argNames = Object.keys(op.inputs);
  const argValues = Object.values(op.inputs);
  if (isBinaryOp(op)) {
    return `${simpleNodeString(argValues[0])} ${binaryOpSymbol(
      op
    )} ${simpleNodeString(argValues[1])}`;
  }

  if (isBracketsOp(op)) {
    // Note, we skip rendering left hand side (obj.) when it is a
    // variable. So x.count is just count
    const obj = argValues[0];
    const key = argValues[1];
    if (obj.nodeType === 'var' && key.nodeType === 'const') {
      return key.val;
    }
    return `${simpleNodeString(obj)}[${simpleNodeString(key)}]`;
  }
  if (isDotChainedOp(op)) {
    // Note, we skip rendering left hand side (obj.) when it is a
    // variable. So x.count is just count
    const obj = argValues[0];
    const leftHandSide =
      obj.nodeType === 'var' ? '' : simpleNodeString(argValues[0]);
    return `${leftHandSide}${opDisplayName(op)}${
      argNames.length > 1
        ? simpleArgsString(_.pickBy(op.inputs, (v, k) => k !== argNames[0]))
        : ''
    }`;
  }
  return `${opDisplayName(op)} ${simpleArgsString(op.inputs)} `;
};

export const simpleNodeString = (node: CGTypes.EditingNode): string =>
  node.nodeType === 'var'
    ? node.varName
    : node.nodeType === 'output'
    ? simpleOpString(node.fromOp)
    : node.nodeType === 'const'
    ? node.type === 'number'
      ? node.val.toString()
      : node.type === 'string'
      ? node.val
      : '?'
    : '-';
